import { isPlatformBrowser } from "@angular/common";
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from "@angular/common/http";
import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import { BatchSalesReport, SalesByBatch } from "@models/batch-sales-report";
import {
  AddCollaboratorDTO,
  Collaborator,
  CollaboratorInvitation,
  PdvSalesScope,
  PdvAccessSettings,
  CollaboratorMutationResponse,
} from "@models/collaborator";
import { EventParticipant } from "@models/event-participants";
import {
  EventOrderFilters,
  EventOrdersPage,
} from "@models/event-order-filters";
import { EventSummary } from "@models/event-summary";
import { Invitation } from "@models/invitation";
import { InviteGuestDTO } from "@models/invite-guest";
import { CourtesyTicketOption } from "@models/courtesy-ticket";
import { Page } from "@models/page";
import { PublicFlowDTO } from "@models/public-flow";
import { SalesChannelSummary } from "@models/sales-channel-summary";
import { SellerSalesSummary } from "@models/seller-sales-summary";
import { ValidatedTickets } from "@models/validated-tickets";
import { PaginatorState } from "primeng/paginator";
import {
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  tap,
  throwError,
} from "rxjs";
import { environment } from "../../environments/environment";
import { TicketBatch } from "../../shared/components/modal-tickets/types/ticket-batch.type";
import { sortBatchesByStartDate } from "../../utils/utils";
import { CheckIn } from "../pages/producer-event-page/validated-summary-tab/check-in-section/check-in-section.component";
import { OrdersPaymentMethodSummary } from "./../../models/orders-payment-method-summary";
import { AuthService } from "./auth.service";
import { LocalStorageService } from "./local-storage.service";
import { SelectedCityService } from "./selected-city.service";
import { SnackBarService } from "./snack-bar.service";
import {
  AuthorizeEventColaboratorRequest,
  CurrentBatch,
  EventInfo,
  EventInfoRequestBody,
  EventsListFilters,
  MyEventsFilters,
} from "./types/events-management-request.type";
import {
  EventCategory,
  EventInfoResponseBody,
  EventInfoResponseData,
  ICreateEventResponse,
  ProducerEvents,
  ProducerEventsDTO,
  RequestTicketScan,
} from "./types/events-management-response.type";

@Injectable({
  providedIn: "root",
})
export class EventsManagementService {
  private readonly apiUrlEvents = environment.API_BACKEND;

  constructor(
    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService,
    private readonly snackbarService: SnackBarService,
    private readonly authService: AuthService,
    private readonly selectedCityService: SelectedCityService,
  ) {}

  platformId = inject(PLATFORM_ID);

  private getToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return this.localStorageService.getItem("auth-token");
    }
    return null;
  }

  private getHttpHeaders(): HttpHeaders {
    const token = this.getToken();
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
      Expires: "0",
    });
  }

  createEvent(
    createEventRequestBody: EventInfoRequestBody,
  ): Observable<ICreateEventResponse> {
    return this.http.post<ICreateEventResponse>(
      `${this.apiUrlEvents}/events`,
      createEventRequestBody,
      { headers: this.getHttpHeaders() },
    );
  }

  updateEvent(
    eventId: number,
    updateEventRequestBody: EventInfoRequestBody,
  ): Observable<EventInfoResponseBody> {
    return this.http.patch<EventInfoResponseBody>(
      `${this.apiUrlEvents}/events/${eventId}`,
      updateEventRequestBody,
      { headers: this.getHttpHeaders() },
    );
  }

  getAllEvents(
    filters: EventsListFilters = {},
  ): Observable<Page<EventInfoResponseBody>> {
    const userInfo = this.authService.getLocalStorageUserInfo();

    const {
      page = 0,
      per_page = 20,
      query,
      city,
      start_date,
      end_date,
      category,
      sort,
    } = filters;

    let params = new HttpParams()
      .set("page", page + 1)
      .set("per_page", per_page);

    if (query) params = params.set("query", query);
    if (city) params = params.set("city", city);
    if (start_date) params = params.set("start_date", start_date);
    if (end_date) params = params.set("end_date", end_date);
    if (category) params = params.set("category", category);
    if (sort) params = params.set("sort", sort);

    return this.http
      .get<Page<EventInfoResponseBody>>(`${this.apiUrlEvents}/events`, {
        headers: this.getHttpHeaders()
          .set("Cache-Control", "no-cache")
          .set("Pragma", "no-cache")
          .set("_ts", Date.now().toString())
          .set("Expires", "0"),
        params,
      })
      .pipe(
        map((response) => {
          if (userInfo) {
            const userStarredSet = new Set(userInfo.starred_events);
            response.data.forEach((event) => {
              event.is_favorite = userStarredSet.has(Number(event.id));
            });
          }

          return response;
        }),
        catchError(() =>
          of({
            data: [],
            meta: {
              total_count: 0,
              total_pages: 0,
              current_page: 0,
              page_size: 10,
            },
          } as Page<EventInfoResponseBody>),
        ),
      );
  }

  getActiveCities(): Observable<string[]> {
    return this.http
      .get<{ cities: string[] }>(`${this.apiUrlEvents}/events/active_cities`)
      .pipe(
        map((response) => response.cities ?? []),
        catchError(() => of([])),
      );
  }

  getEventsByName(query: string) {
    return this.http
      .get<Page<EventInfoResponseBody>>(
        `${this.apiUrlEvents}/events/search_events`,
        {
          params: {
            query,
          },
        },
      )
      .pipe(
        catchError(() =>
          of({
            data: [],
            meta: {
              total_count: 0,
              total_pages: 0,
              current_page: 0,
              page_size: 10,
            },
          } as Page<EventInfoResponseBody>),
        ),
      );
  }

  getTrendingEvents(): Observable<EventInfoResponseBody[]> {
    return this.getAllEvents({
      sort: "trending",
      city: this.selectedCityService.selectedCity() ?? undefined,
    }).pipe(map((response) => response.data));
  }

  getEventsCategories(): Observable<EventCategory[]> {
    return this.http.get<EventCategory[]>(
      `${this.apiUrlEvents}/event_categories`,
      { headers: this.getHttpHeaders() },
    );
  }

  getEventInfo(event_id: number): Observable<EventInfo> {
    const userStarredEvents =
      this.authService.getLocalStorageUserInfo()?.starred_events;

    const userStarredSet = new Set(userStarredEvents);
    return this.http
      .get<EventInfo>(`${this.apiUrlEvents}/events/${event_id}`)
      .pipe(
        map((eventInfo) => {
          if (userStarredEvents) {
            eventInfo.is_favorite = userStarredSet.has(Number(eventInfo.id));
          }
          return eventInfo;
        }),
      );
  }

  getEventCurrentBatch(event_id: number): Observable<CurrentBatch> {
    return this.http.get<CurrentBatch>(
      `${this.apiUrlEvents}/events/${event_id}/batches/current_batch`,
    );
  }

  getEventBatches(event_id: number): Observable<TicketBatch[]> {
    return this.http
      .get<TicketBatch[]>(`${this.apiUrlEvents}/events/${event_id}/batches`)
      .pipe(map((response) => sortBatchesByStartDate(response)));
  }

  getEventSalesByBatches(event_id: number): Observable<SalesByBatch[]> {
    return this.http.get<SalesByBatch[]>(
      `${this.apiUrlEvents}/events/${event_id}/sales_by_batches`,
    );
  }

  getEventsByCategory(category: number): Observable<EventInfoResponseData> {
    return this.http.get<EventInfoResponseData>(
      `${this.apiUrlEvents}/events/by_category/${category}`,
    );
  }

  getEventsByOrganizer(
    organizerId: number,
    filters: EventsListFilters = {},
  ): Observable<Page<EventInfoResponseBody>> {
    const {
      page = 0,
      per_page = 20,
      query,
      city,
      start_date,
      end_date,
      category,
      status,
    } = filters;

    let params = new HttpParams()
      .set("page", page + 1)
      .set("per_page", per_page);

    if (query) params = params.set("query", query);
    if (city) params = params.set("city", city);
    if (start_date) params = params.set("start_date", start_date);
    if (end_date) params = params.set("end_date", end_date);
    if (category) params = params.set("category", category);
    if (status) params = params.set("status", status);

    return this.http
      .get<
        Page<EventInfoResponseBody>
      >(`${this.apiUrlEvents}/events/by_organizer/${organizerId}`, { params })
      .pipe(
        // O endpoint retorna 404 (e não 200 com lista vazia) quando nenhum
        // evento corresponde aos filtros. Tratamos como "sem resultados".
        catchError((err) => {
          if (err.status === 404) {
            return of({
              data: [],
              meta: {
                total_count: 0,
                total_pages: 0,
                current_page: page + 1,
                page_size: per_page,
              },
            } as Page<EventInfoResponseBody>);
          }
          return throwError(() => err);
        }),
      );
  }

  getEventsForOrganizer(): Observable<EventInfoResponseBody[]> {
    return this.http.get<EventInfoResponseBody[]>(
      `${this.apiUrlEvents}/events/show_for_organizer`,
    );
  }

  getMyEvents(
    page = 0,
    pageSize?: number,
    filters: MyEventsFilters = {},
  ): Observable<Page<EventInfoResponseBody>> {
    let params = new HttpParams().set("page", page + 1);
    if (pageSize) params = params.set("per_page", pageSize);

    if (filters.query?.trim()) {
      params = params.set("query", filters.query.trim());
    }
    if (filters.status) {
      params = params.set("status", filters.status);
    }
    if (filters.min_revenue !== undefined && filters.min_revenue !== null) {
      params = params.set("min_revenue", filters.min_revenue);
    }
    if (filters.max_revenue !== undefined && filters.max_revenue !== null) {
      params = params.set("max_revenue", filters.max_revenue);
    }

    return this.http
      .get<
        Page<EventInfoResponseBody>
      >(`${this.apiUrlEvents}/events/my_events`, { headers: this.getHttpHeaders(), params: params })
      .pipe(
        catchError(() =>
          of({
            data: [],
            meta: {
              total_count: 0,
              total_pages: 0,
              current_page: 0,
              page_size: 10,
            },
          } as Page<EventInfoResponseBody>),
        ),
      );
  }

  getMyAssignedEvents(
    page = 0,
    pageSize?: number,
  ): Observable<Page<EventInfoResponseBody>> {
    let params = new HttpParams().set("page", page + 1);
    if (pageSize) params = params.set("per_page", pageSize);
    return this.http
      .get<
        Page<EventInfoResponseBody>
      >(`${this.apiUrlEvents}/events/my_events_collab`, { headers: this.getHttpHeaders(), params: params })
      .pipe(
        catchError(() =>
          of({
            data: [],
            meta: {
              total_count: 0,
              total_pages: 0,
              current_page: 0,
              page_size: 10,
            },
          } as Page<EventInfoResponseBody>),
        ),
      );
  }

  listMyEventsMerged(
    page = 0,
    pageSize = 250,
  ): Observable<EventInfoResponseBody[]> {
    return forkJoin({
      asOrganizer: this.getMyEvents(page, pageSize),
      asCollaborator: this.getMyAssignedEvents(page, pageSize),
    }).pipe(
      map(({ asOrganizer, asCollaborator }) => {
        const byId = new Map<number, EventInfoResponseBody>();
        for (const event of [...asOrganizer.data, ...asCollaborator.data]) {
          const id = Number(event.id);
          if (!Number.isFinite(id)) {
            continue;
          }
          if (!byId.has(id)) {
            byId.set(id, event);
          }
        }
        return [...byId.values()];
      }),
    );
  }

  getMyEventById(eventId: string | number): Observable<EventInfoResponseBody> {
    return this.http.get<EventInfoResponseBody>(
      `${this.apiUrlEvents}/events/my_events/${eventId}`,
    );
  }

  deleteEvent(eventId: number): Observable<HttpResponse<void>> {
    return this.http.delete<void>(`${this.apiUrlEvents}/events/${eventId}`, {
      observe: "response",
    });
  }

  cancelEvent(eventId: number): Observable<{ message: string }> {
    return this.http
      .post<{
        message: string;
      }>(`${this.apiUrlEvents}/events/${eventId}/cancel`, {})
      .pipe(
        tap((response) =>
          this.snackbarService.openSnackBar(
            this.resolveMessage(
              response?.message,
              "Solicitação de cancelamento enviada com sucesso.",
            ),
            "Fechar",
            "success",
          ),
        ),
        catchError((error) => {
          this.snackbarService.openSnackBar(
            this.resolveMessage(
              error?.error?.error ?? error?.error?.message,
              "Não foi possível solicitar o cancelamento. Tente novamente.",
            ),
            "Fechar",
            "error",
          );
          return throwError(() => error);
        }),
      );
  }

  /** Usa a mensagem da API apenas quando ela for um texto válido. */
  private resolveMessage(message: unknown, fallback: string): string {
    return typeof message === "string" && message.trim()
      ? message.trim()
      : fallback;
  }

  updateEventStarred(eventId: string, starred: boolean): Observable<any> {
    return this.http.patch<any>(`${this.apiUrlEvents}/events/${eventId}`, {
      starred: starred,
    });
  }

  authorizeEventColaborator({
    event_id,
    user_id,
  }: AuthorizeEventColaboratorRequest): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrlEvents}/events/${event_id}/authorizations`,
      { user_id },
      { headers: this.getHttpHeaders() },
    );
  }

  validateTicketScan(
    event_id: number,
    data: RequestTicketScan,
  ): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrlEvents}/events/${event_id}/ticket_scans`,
      data,
      {
        headers: this.getHttpHeaders(),
      },
    );
  }

  getEventScans(event_id: number): Observable<CheckIn[]> {
    return this.http.get<CheckIn[]>(
      `${this.apiUrlEvents}/events/${event_id}/ticket_scans`,
      { headers: this.getHttpHeaders() },
    );
  }

  sendGuestInvite(event_id: number, data: InviteGuestDTO) {
    return this.http
      .post<InviteGuestDTO>(
        `${this.apiUrlEvents}/events/${event_id}/invitations`,
        data,
        {
          headers: this.getHttpHeaders(),
        },
      )
      .pipe(
        tap(() =>
          this.snackbarService.openSnackBar(
            "Convite enviado com sucesso",
            "Fechar",
            "success",
          ),
        ),
        catchError((err) => {
          if (err.status === 422) {
            this.snackbarService.openSnackBar(
              err.error?.error || "Não foi possível enviar a cortesia.",
              "Fechar",
              "error",
            );
          }
          return throwError(() => err);
        }),
      );
  }

  getCourtesyTickets(
    eventId: number,
  ): Observable<{ tickets: CourtesyTicketOption[] }> {
    return this.http.get<{ tickets: CourtesyTicketOption[] }>(
      `${this.apiUrlEvents}/events/${eventId}/courtesy_tickets`,
      { headers: this.getHttpHeaders() },
    );
  }

  resendInvite(event_id: number, invitation_id: number) {
    return this.http
      .post<InviteGuestDTO>(
        `${this.apiUrlEvents}/events/${event_id}/invitations/${invitation_id}/resend`,
        {},
      )
      .pipe(
        tap(() =>
          this.snackbarService.openSnackBar(
            "Convite reenviado com sucesso",
            "Fechar",
            "success",
          ),
        ),
        catchError((err) => {
          if (err.status === 422) {
            this.snackbarService.openSnackBar(
              "O convite já foi aceito",
              "Fechar",
              "error",
            );
          } else if (err.status === 404) {
            this.snackbarService.openSnackBar(
              "Evento não encontrado",
              "Fechar",
              "error",
            );
          } else {
            this.snackbarService.openSnackBar(
              "Erro ao reenviar convite",
              "Fechar",
              "errpr",
            );
          }
          return err;
        }),
      );
  }

  getEventSummary(event_id: number) {
    return this.http.get<EventSummary>(
      `${this.apiUrlEvents}/events/${event_id}/summary`,
    );
  }

  downloadOrdersReport(event_id: number) {
    return this.http.get(
      `${this.apiUrlEvents}/events/${event_id}/orders_report`,
      {
        headers: this.getHttpHeaders(),
        responseType: "blob",
      },
    );
  }

  downloadCortesiasReport(event_id: number) {
    return this.http.get(
      `${this.apiUrlEvents}/events/${event_id}/cortesias_report`,
      {
        headers: this.getHttpHeaders(),
        responseType: "blob",
      },
    );
  }

  downloadCollaboratorsReport(event_id: number) {
    return this.http.get(
      `${this.apiUrlEvents}/events/${event_id}/collaborators_report`,
      {
        headers: this.getHttpHeaders(),
        responseType: "blob",
      },
    );
  }

  downloadTicketScansReport(event_id: number) {
    return this.http.get(
      `${this.apiUrlEvents}/events/${event_id}/ticket_scans_report`,
      {
        headers: this.getHttpHeaders(),
        responseType: "blob",
      },
    );
  }

  getEventParticipants(
    event_id: number,
    pageEvent: PaginatorState | null,
    searchTerm?: string,
    options?: EventOrderFilters & {
      sort?: string | null;
      direction?: "asc" | "desc";
      status?: string | null;
    },
  ) {
    const page = pageEvent?.page ?? 0;
    let params = new HttpParams()
      .set("page", page + 1)
      .set("per_page", pageEvent?.rows ?? 10);

    if (searchTerm) {
      params = params.set("name", searchTerm);
    }
    if (options?.sort) {
      params = params
        .set("sort", options.sort)
        .set("direction", options.direction ?? "desc");
    }
    if (options?.status) {
      params = params.set("status", options.status);
    }

    const filterKeys = [
      "search",
      "sales_channel",
      "payment_method",
      "paid_from",
      "paid_until",
      "time_zone",
      "operator_id",
      "terminal_id",
    ] as const;
    for (const key of filterKeys) {
      const value = options?.[key];
      if (value) params = params.set(key, value);
    }
    if (options?.include_filter_options)
      params = params.set("include_filter_options", "true");

    return this.http.get<EventOrdersPage>(
      `${this.apiUrlEvents}/events/${event_id}/show_buyers`,
      { params, headers: this.getHttpHeaders() },
    );
  }

  getOrdersByPaymentMethod(event_id: number) {
    return this.http.get<OrdersPaymentMethodSummary[]>(
      `${this.apiUrlEvents}/events/${event_id}/show_orders_by_payment_method`,
      { headers: this.getHttpHeaders() },
    );
  }

  // Total do evento agrupado por canal de venda (online, pdv, affiliate).
  // O backend responde 404 quando não há vendas; tratamos como lista vazia.
  getSalesByChannel(event_id: number) {
    return this.http
      .get<
        SalesChannelSummary[]
      >(`${this.apiUrlEvents}/events/${event_id}/show_sales_by_channel`, { headers: this.getHttpHeaders() })
      .pipe(catchError(() => of([])));
  }

  getSellerSalesSummary(
    eventId: number,
    page = 0,
    perPage = 5,
  ): Observable<Page<SellerSalesSummary>> {
    const params = new HttpParams()
      .set("event_id", eventId)
      .set("page", page + 1)
      .set("per_page", perPage);

    return this.http.get<Page<SellerSalesSummary>>(
      `${this.apiUrlEvents}/my_sales/sellers_summary`,
      { headers: this.getHttpHeaders(), params },
    );
  }

  getValidatedTicketsBySector(event_id: number) {
    return this.http.get<ValidatedTickets[]>(
      `${this.apiUrlEvents}/events/${event_id}/ticket_scans_by_sector`,
      { headers: this.getHttpHeaders() },
    );
  }

  getTicketsByCategorySummary(event_id: number) {
    return this.http
      .get<
        BatchSalesReport[]
      >(`${this.apiUrlEvents}/events/${event_id}/tickets_by_category`, { headers: this.getHttpHeaders() })
      .pipe(catchError(() => of([])));
  }

  getInvitationsByEventId(
    event_id: number,
    pageEvent: PaginatorState | null,
    searchTerm?: string,
  ) {
    const page = pageEvent?.page ?? 0;
    let params = new HttpParams()
      .set("page", page + 1)
      .set("per_page", pageEvent?.rows ?? 10);

    if (searchTerm) {
      params = params.set("name", searchTerm);
    }

    return this.http.get<Page<Invitation>>(
      `${this.apiUrlEvents}/events/${event_id}/invitations`,
      { params, headers: this.getHttpHeaders() },
    );
  }

  getParticipantDetails(event_id: number, order_id: number) {
    return this.http.get<EventParticipant>(
      `${this.apiUrlEvents}/events/${event_id}/show_buyer_by_order/${order_id}`,
      { headers: this.getHttpHeaders() },
    );
  }

  getEventsScanByTime(event_id: number) {
    return this.http
      .get<PublicFlowDTO>(
        `${this.apiUrlEvents}/events/${event_id}/ticket_scans_by_time`,
        { headers: this.getHttpHeaders() },
      )
      .pipe(
        catchError((err) => {
          console.log(err);
          return of({ validated_flow: [] });
        }),
      );
  }

  // COLLABORATORS

  updatePdvSettings(
    eventId: number,
    person: Collaborator | CollaboratorInvitation,
    settings: PdvAccessSettings,
    pending: boolean,
  ) {
    return this.http.put<CollaboratorMutationResponse>(
      `${this.apiUrlEvents}/events/${eventId}/update_collaborator_role`,
      {
        pdv_settings: { id: person.id, pending, ...settings },
      },
    );
  }

  generateMyPdvPin(eventId: number) {
    return this.http.post<CollaboratorMutationResponse>(
      `${this.apiUrlEvents}/events/${eventId}/pdv_approver_pin`,
      {},
    );
  }

  updatePdvPermission(
    eventId: number,
    person: Collaborator | CollaboratorInvitation,
    scope: PdvSalesScope,
    pending: boolean,
  ) {
    const body = pending
      ? {
          collaborator: {
            email: person.email,
            role: person.role,
            user_phone_number: person.phone,
            pdv_sales_scope: scope,
          },
        }
      : {
          user_id: (person as Collaborator).user_id,
          role: person.role,
          pdv_sales_scope: scope,
        };
    return this.http
      .put(
        `${this.apiUrlEvents}/events/${eventId}/update_collaborator_role`,
        body,
      )
      .pipe(
        tap(() =>
          this.snackbarService.openSnackBar(
            "Permissão de venda atualizada",
            "Fechar",
            "success",
          ),
        ),
        catchError((error) => {
          this.snackbarService.openSnackBar(
            "Não foi possível atualizar a permissão",
            "Fechar",
            "error",
          );
          return throwError(() => error);
        }),
      );
  }

  addCollaborator(event_id: number, addCollaboratorDTO: AddCollaboratorDTO) {
    return this.http
      .post<CollaboratorMutationResponse>(
        `${this.apiUrlEvents}/events/${event_id}/add_collaborator`,
        addCollaboratorDTO,
        { headers: this.getHttpHeaders() },
      )
      .pipe(
        tap(() =>
          this.snackbarService.openSnackBar(
            "Colaborador convidado com sucesso",
            "Fechar",
            "success",
          ),
        ),
        catchError((err) => {
          this.snackbarService.openSnackBar(
            "Erro ao convidar colaborador",
            "Fechar",
            "error",
          );

          return throwError(() => err);
        }),
      );
  }

  removeCollaborator(event_id: number, collaborator: Collaborator) {
    const body = {
      email: collaborator.email,
      role: collaborator.role,
      user_id: collaborator.user_id,
    };

    return this.http
      .delete<AddCollaboratorDTO>(
        `${this.apiUrlEvents}/events/${event_id}/remove_collaborator`,
        { body },
      )
      .pipe(
        tap(() =>
          this.snackbarService.openSnackBar(
            "Colaborador removido com sucesso",
            "Fechar",
            "success",
          ),
        ),
        catchError((err) => {
          this.snackbarService.openSnackBar(
            "Erro ao remover colaborador",
            "Fechar",
            "error",
          );

          return err;
        }),
      );
  }

  getCollaborators(
    event_id: string,
    pageEvent: PaginatorState | null,
    searchTerm?: string,
  ) {
    const page = pageEvent?.page ?? 0;
    let params = new HttpParams()
      .set("page", page + 1)
      .set("per_page", pageEvent?.rows ?? 10);

    if (searchTerm) {
      params = params.set("name", searchTerm);
    }

    return this.http.get<Page<Collaborator>>(
      `${this.apiUrlEvents}/events/${event_id}/collaborators`,
      { params },
    );
  }

  getCollaboratorsInvitations(
    event_id: string,
    pageEvent: PaginatorState | null,
    searchTerm?: string,
  ) {
    const page = pageEvent?.page ?? 0;
    let params = new HttpParams()
      .set("page", page + 1)
      .set("per_page", pageEvent?.rows ?? 10);

    if (searchTerm) {
      params = params.set("email", searchTerm);
    }
    return this.http.get<Page<CollaboratorInvitation>>(
      `${this.apiUrlEvents}/events/${event_id}/collaborator_invitations`,
      { params },
    );
  }

  getFollowedProducersEvents(): Observable<ProducerEvents[]> {
    return this.http
      .get<ProducerEventsDTO>(`${this.apiUrlEvents}/events/following`, {
        headers: this.getHttpHeaders(),
      })
      .pipe(map((response) => mapApiResponseToProducerEvents(response)));
  }

  /**
   * Downloads event overview report as PDF
   * @param eventId The event ID
   */
  downloadEventOverviewReport(eventId: number): Observable<Blob> {
    const headers = this.getHttpHeaders().set("Accept", "application/pdf");

    return this.http.get(
      `${this.apiUrlEvents}/events/${eventId}/event_overview_report`,
      {
        headers,
        responseType: "blob",
      },
    );
  }
}

export function mapApiResponseToProducerEvents(
  response: ProducerEventsDTO,
): ProducerEvents[] {
  return Object.entries(response).map(
    ([producerName, { events }]): ProducerEvents => ({
      producerName,
      events,
    }),
  );
}
